// Thin JSON client for the Express API.
//
// - Sends the session cookie (same origin).
// - GET requests are retried on network errors and 502/503/504, honouring
//   Retry-After: the server answers 503 while the database is briefly away.
// - Writes are not retried automatically (they may have been applied); the
//   caller shows the error and the user can try again.
// - 401 outside the sign-in endpoints means the session is gone: listeners
//   (the session store) send the user to the login page.

import { connection } from './connection.svelte';

export class ApiError extends Error {
  status: number;
  code: string | null;
  body: unknown;
  constructor(status: number, message: string, code: string | null = null, body: unknown = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
  get retryable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Do not trigger the global sign-out on 401 (sign-in calls). */
  allowUnauthorized?: boolean;
  /** Override automatic retries (default: on for GET). */
  retries?: number;
}

const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

const OFFLINE_MESSAGE = 'Geen verbinding met Timepeak. Controleer je internet en probeer het opnieuw.';
const UNAVAILABLE_MESSAGE = 'Timepeak is even niet bereikbaar. Probeer het over een paar seconden opnieuw.';

async function once(method: string, url: string, opts: RequestOptions): Promise<Response> {
  const timeout = AbortSignal.timeout(opts.timeoutMs ?? 20000);
  const signal = opts.signal && 'any' in AbortSignal
    ? (AbortSignal as unknown as { any(s: AbortSignal[]): AbortSignal }).any([opts.signal, timeout])
    : opts.signal ?? timeout;
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  return fetch(url, { method, headers, body, credentials: 'same-origin', signal });
}

async function parse(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const type = res.headers.get('content-type') || '';
  if (type.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  const text = await res.text();
  return text || null;
}

export async function request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const maxRetries = opts.retries ?? (method === 'GET' ? 3 : 0);
  let attempt = 0;

  for (;;) {
    let res: Response;
    try {
      res = await once(method, url, opts);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError' && opts.signal?.aborted) throw err;
      connection.reportFailure();
      if (attempt < maxRetries) {
        attempt += 1;
        await sleep(400 * 2 ** attempt, opts.signal);
        continue;
      }
      throw new ApiError(0, navigator.onLine === false ? OFFLINE_MESSAGE : UNAVAILABLE_MESSAGE, 'NETWORK');
    }

    if (res.status === 502 || res.status === 503 || res.status === 504) {
      connection.reportFailure();
      if (attempt < maxRetries) {
        attempt += 1;
        const retryAfter = Number(res.headers.get('retry-after'));
        const delay = Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter * 1000, 8000) : 600 * 2 ** attempt;
        await sleep(delay, opts.signal);
        continue;
      }
      const data = (await parse(res)) as { error?: string; code?: string } | null;
      throw new ApiError(res.status, data?.error || UNAVAILABLE_MESSAGE, data?.code || 'SERVICE_UNAVAILABLE', data);
    }

    connection.reportSuccess();
    const data = await parse(res);

    if (res.status === 401 && !opts.allowUnauthorized) {
      unauthorizedListeners.forEach((fn) => fn());
    }
    if (!res.ok) {
      const payload = (data && typeof data === 'object' ? data : {}) as { error?: string; message?: string; code?: string };
      const message = payload.error || payload.message || (typeof data === 'string' && data.length < 200 ? data : `Er ging iets mis (${res.status})`);
      throw new ApiError(res.status, message, payload.code || null, data);
    }
    return data as T;
  }
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  del: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, { ...opts, body })
};

export function errorMessage(err: unknown, fallback = 'Er ging iets mis. Probeer het opnieuw.'): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
